import React, { useState } from 'react';
import { CheckCircle, ArrowRight, BarChart, Calendar, Users, FileSpreadsheet, CreditCard, LifeBuoy, MessageSquare, FileQuestion, PhoneCall, Mail, Laptop, Smartphone, Database, Shield, LayoutDashboard, DollarSign, BookOpen, Receipt, Package, FileText, Settings, ShoppingCart, Building2, ArrowUpRight, ArrowDownRight, Wrench, ChevronUp, ChevronDown, Brain } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProductLayout } from '../../components/layouts/ProductLayout';
import { AnimatedSection } from '../../components/AnimatedSection';
import { Wave } from '../../components/Wave';
import { Link } from 'react-router-dom';
import { useSEO } from '../../hooks/useSEO';
import { getPageSEO } from '../../data/seoData';

export const QuickCont = () => {
  // SEO Configuration
  useSEO(getPageSEO('quickcont'));

  const [showAllModules, setShowAllModules] = useState(false);
  const [isAnnual, setIsAnnual] = useState(false);
  const productConfig = {
    name: 'QuickCont',
    color: 'teal',
    gradientFrom: 'from-teal-600',
    gradientTo: 'to-teal-700',
    description: 'Sistema ERP integral que unifica contabilidad, facturación, inventario, nómina y más en una sola plataforma. Con asistente inteligente integrado, reportes personalizados y cumplimiento fiscal automatizado para optimizar la gestión completa de tu empresa.',
    links: [{
      label: 'Inicio',
      path: '/quickcont'
    }, {
      label: 'Módulos',
      path: '/quickcont#caracteristicas'
    }, {
      label: 'Precios',
      path: '/quickcont#precios'
    }, {
      label: 'Soporte',
      path: '/quickcont#soporte'
    }],
    externalLink: {
      label: 'Acceder',
      url: 'https://quickcont.ipse.com.ec'
    }
  };
  const features = [{
    icon: <BarChart className="w-8 h-8 text-teal-600" />,
    title: 'Reportes Financieros',
    description: 'Genera reportes detallados y visualiza el rendimiento de tu empresa en tiempo real con gráficos interactivos y métricas personalizables'
  }, {
    icon: <Calendar className="w-8 h-8 text-teal-600" />,
    title: 'Planificación Fiscal',
    description: 'Mantén el control de tus obligaciones fiscales con alertas automáticas, calendarios de vencimientos y proyecciones de impuestos'
  }, {
    icon: <Users className="w-8 h-8 text-teal-600" />,
    title: 'Gestión de Nómina',
    description: 'Administra fácilmente los pagos, beneficios, vacaciones y retenciones de tus empleados con cálculos automáticos y precisos'
  }, {
    icon: <FileSpreadsheet className="w-8 h-8 text-teal-600" />,
    title: 'Facturación Electrónica',
    description: 'Emite y gestiona facturas electrónicas que cumplen con todas las normativas vigentes y se integran con tu contabilidad'
  }, {
    icon: <Database className="w-8 h-8 text-teal-600" />,
    title: 'Conciliación Bancaria',
    description: 'Automatiza la conciliación de tus cuentas bancarias con importación directa de movimientos y detección inteligente de coincidencias'
  }, {
    icon: <Shield className="w-8 h-8 text-teal-600" />,
    title: 'Seguridad Avanzada',
    description: 'Protege tu información financiera con encriptación de datos, autenticación de dos factores y respaldos automáticos'
  }];
  const pricingPlans = [{
    name: 'Básico',
    price: '$49',
    period: 'mes',
    description: 'Ideal para autónomos y pequeñas empresas',
    features: ['Hasta 500 transacciones mensuales', '2 usuarios incluidos', 'Reportes básicos', 'Facturación electrónica', 'Soporte por email'],
    cta: 'Comenzar prueba gratuita',
    highlighted: false
  }, {
    name: 'Profesional',
    price: '$99',
    period: 'mes',
    description: 'Perfecto para empresas en crecimiento',
    features: ['Hasta 2,000 transacciones mensuales', '5 usuarios incluidos', 'Reportes avanzados', 'Facturación electrónica ilimitada', 'Gestión de nómina', 'Soporte prioritario'],
    cta: 'Comenzar prueba gratuita',
    highlighted: true
  }, {
    name: 'Empresarial',
    price: '$199',
    period: 'mes',
    description: 'Solución completa para grandes empresas',
    features: ['Transacciones ilimitadas', 'Usuarios ilimitados', 'Reportes personalizados', 'API para integraciones', 'Módulos adicionales incluidos', 'Soporte dedicado 24/7'],
    cta: 'Contactar ventas',
    highlighted: false
  }];
  const supportOptions = [{
    icon: <MessageSquare className="w-10 h-10 text-teal-600" />,
    title: 'Chat en vivo',
    description: 'Conecta con nuestro equipo de soporte en tiempo real para resolver tus dudas de forma inmediata.'
  }, {
    icon: <FileQuestion className="w-10 h-10 text-teal-600" />,
    title: 'Base de conocimiento',
    description: 'Accede a cientos de artículos, tutoriales y guías para aprender a utilizar todas las funciones de QuickCont.'
  }, {
    icon: <PhoneCall className="w-10 h-10 text-teal-600" />,
    title: 'Soporte telefónico',
    description: 'Nuestros especialistas están disponibles para resolver problemas complejos mediante asistencia telefónica.'
  }, {
    icon: <Mail className="w-10 h-10 text-teal-600" />,
    title: 'Soporte por correo',
    description: 'Envía tus consultas técnicas por email y recibe respuesta en menos de 24 horas.'
  }];
  return <ProductLayout product={productConfig}>
      <section className="bg-gradient-to-b from-teal-800 via-teal-700 to-teal-600">
        <Wave color="#f9fafb" className="bg-teal-600" pattern="pattern1" />
      </section>
      <section id="caracteristicas" className="py-20 bg-gray-50">
        <AnimatedSection className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Módulos del Sistema</h2>
              <p className="text-gray-600 max-w-3xl mx-auto">
                QuickCont es un sistema ERP completo que integra todos los
                aspectos de tu negocio en una única plataforma. Desde la gestión
                financiera hasta el control de inventario, cada módulo está
                diseñado para maximizar la eficiencia y precisión de tus
                operaciones.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <motion.div className="col-span-full lg:col-span-3 bg-gradient-to-r from-blue-500 to-blue-600 rounded-xl p-8 text-white shadow-xl" whileHover={{
              scale: 1.02
            }}>
                <div className="flex items-center mb-6">
                  <div className="relative">
                    <Brain className="w-8 h-8 mr-3" />
                    <motion.div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-300 rounded-full" animate={{
                    scale: [1, 1.2, 1],
                    opacity: [1, 0.8, 1]
                  }} transition={{
                    duration: 2,
                    repeat: Infinity
                  }} />
                  </div>
                  <h3 className="text-2xl font-bold flex items-center">
                    💡 IPSE IA - Asistente Inteligente
                  </h3>
                </div>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-colors">
                    <p className="font-medium">Análisis Predictivo</p>
                    <p className="text-sm opacity-90">
                      IPSE IA anticipa tendencias de ventas y comportamientos de
                      mercado con precisión
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-colors">
                    <p className="font-medium">Detección Proactiva</p>
                    <p className="text-sm opacity-90">
                      IPSE IA identifica automáticamente errores y patrones
                      inusuales en tiempo real
                    </p>
                  </div>
                  <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 hover:bg-white/20 transition-colors">
                    <p className="font-medium">Asistencia Continua</p>
                    <p className="text-sm opacity-90">
                      IPSE IA brinda soporte inteligente 24/7 para consultas
                      operativas y técnicas
                    </p>
                  </div>
                </div>
              </motion.div>
              <motion.div className="col-span-full lg:col-span-3 bg-gradient-to-r from-teal-500 to-teal-600 rounded-xl p-8 text-white shadow-xl mt-8" whileHover={{
              scale: 1.02
            }}>
                <h3 className="text-2xl font-bold mb-4 flex items-center">
                  <LayoutDashboard className="w-8 h-8 mr-3" />
                  Dashboard Ejecutivo
                </h3>
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="font-medium">Gráficos Dinámicos</p>
                    <p className="text-sm opacity-90">
                      Visualización en tiempo real de ventas e ingresos vs
                      egresos
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="font-medium">Control de Cartera</p>
                    <p className="text-sm opacity-90">
                      Seguimiento detallado de plazos y vencimientos
                    </p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-4">
                    <p className="font-medium">Top Clientes</p>
                    <p className="text-sm opacity-90">
                      Análisis de los 10 mejores clientes y sus montos
                    </p>
                  </div>
                </div>
              </motion.div>
              <AnimatePresence initial={false}>
                {[{
                title: 'Ventas',
                icon: <Receipt className="w-8 h-8 text-teal-600" />,
                features: ['Facturación Electrónica', 'Guías de Remisión', 'Notas de Venta', 'Reportes de Ventas'],
                highlight: true
              }, {
                title: 'Cuentas por Cobrar',
                icon: <ArrowUpRight className="w-8 h-8 text-teal-600" />,
                features: ['Control de Facturas', 'Seguimiento de Vencimientos', 'Historial de Pagos', 'Reportes de Cartera']
              }, {
                title: 'Cuentas por Pagar',
                icon: <ArrowDownRight className="w-8 h-8 text-teal-600" />,
                features: ['Gestión de Facturas', 'Plazos de Pago', 'Historial de Pagos', 'Reportes de Proveedores']
              }, {
                title: 'Bancos',
                icon: <Building2 className="w-8 h-8 text-teal-600" />,
                features: ['Depósitos Bancarios', 'Notas de Crédito/Débito', 'Cheques Posfechados', 'Conciliación Bancaria']
              }, {
                title: 'Contabilidad',
                icon: <BookOpen className="w-8 h-8 text-teal-600" />,
                features: ['Plan de Cuentas', 'Comprobantes Diario', 'Estados Financieros', 'Cierre de Año']
              }, {
                title: 'Talento Humano',
                icon: <Users className="w-8 h-8 text-teal-600" />,
                features: ['Gestión de Empleados', 'Roles de Pago', 'Departamentos', 'Cargos']
              }, ...(showAllModules ? [{
                title: 'Tributario',
                icon: <FileText className="w-8 h-8 text-teal-600" />,
                features: ['Formularios 103 y 104', 'Reportes Fiscales', 'Comprobantes Electrónicos', 'Autorizaciones']
              }, {
                title: 'Compras',
                icon: <ShoppingCart className="w-8 h-8 text-teal-600" />,
                features: ['Comprobantes de Compra', 'Orden de Compra', 'Reportes de Compras', 'Retenciones']
              }, {
                title: 'Inventario',
                icon: <Package className="w-8 h-8 text-teal-600" />,
                features: ['Control de Existencias', 'Kardex', 'Movimientos', 'Transferencias de Bodega']
              }, {
                title: 'Caja',
                icon: <DollarSign className="w-8 h-8 text-teal-600" />,
                features: ['Arqueo de Caja', 'Cuadre de Caja', 'Cuadre Administrativo', 'Reportes de Caja']
              }, {
                title: 'Mantenimientos',
                icon: <div className="w-8 h-8 text-teal-600" />,
                features: ['Gestión de Bodegas', 'Categorías de Producto', 'Centro de Costos', 'Tipos de Cliente']
              }, {
                title: 'Parámetros',
                icon: <Settings className="w-8 h-8 text-teal-600" />,
                features: ['Diseño de Reportes', 'Parámetros del Sistema', 'Perfil de Empresa', 'Perfiles de Usuarios']
              }, {
                title: 'Herramientas',
                icon: <Wrench className="w-8 h-8 text-teal-600" />,
                features: ['Reporte de Inconsistencias', 'Compras Detallado', 'Notas de Crédito/Débito', 'Reporte de Personas']
              }, {
                title: 'Reportes',
                icon: <BarChart className="w-8 h-8 text-teal-600" />,
                features: ['Documentos Pendientes', 'Estado de Cuenta', 'Flujo de Vencimientos', 'Reportes CXC/CXP']
              }] : [])].map((module, index) => <motion.div key={index} layout initial={{
                opacity: 0,
                y: 20
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -20
              }} transition={{
                duration: 0.2,
                delay: index * 0.05
              }} className={`bg-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all ${module.highlight ? 'ring-2 ring-teal-500' : ''}`} whileHover={{
                y: -5
              }}>
                    <div className="mb-4">{module.icon}</div>
                    <h3 className="text-xl font-semibold mb-4">
                      {module.title}
                    </h3>
                    <ul className="space-y-2">
                      {module.features.map((feature, i) => <li key={i} className="flex items-start">
                          <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-600">{feature}</span>
                        </li>)}
                    </ul>
                  </motion.div>)}
              </AnimatePresence>
            </div>
            <div className="mt-12 text-center">
              <motion.button whileHover={{
              scale: 1.05
            }} whileTap={{
              scale: 0.95
            }} onClick={() => {
              setShowAllModules(!showAllModules);
              if (showAllModules) {
                // If we're hiding modules, scroll to the modules section
                const modulesSection = document.getElementById('caracteristicas');
                if (modulesSection) {
                  modulesSection.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                  });
                }
              }
            }} className="bg-teal-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-teal-700 transition-colors inline-flex items-center">
                {showAllModules ? <>
                    Ver menos módulos
                    <ChevronUp className="ml-2 h-5 w-5" />
                  </> : <>
                    Ver todos los módulos
                    <ChevronDown className="ml-2 h-5 w-5" />
                  </>}
              </motion.button>
            </div>
          </div>
        </AnimatedSection>
      </section>

      <Wave color="white" className="bg-gray-50" pattern="pattern2" />
      <AnimatedSection className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Potencia tu Gestión Financiera
              </h2>
              <p className="text-gray-600 mb-8">
                QuickCont te permite tomar el control total de tus finanzas
                empresariales con un sistema integral que automatiza tareas
                repetitivas, minimiza errores y te proporciona información
                valiosa en tiempo real para la toma de decisiones estratégicas.
              </p>
              <ul className="space-y-4">
                {['Interfaz intuitiva adaptada a usuarios sin experiencia contable', 'Reportes personalizables con visualizaciones interactivas', 'Integración con sistemas bancarios y plataformas de comercio electrónico', 'Soporte técnico especializado disponible todos los días', 'Actualizaciones automáticas con las últimas normativas fiscales'].map((item, index) => <li key={index} className="flex items-center">
                    <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" />
                    <span>{item}</span>
                  </li>)}
              </ul>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?w=800&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Dashboard QuickCont" className="rounded-lg shadow-2xl" />
              <div className="absolute inset-0 bg-gradient-to-tr from-teal-600/20 to-transparent rounded-lg" />
            </div>
          </div>
        </div>
      </AnimatedSection>

      <Wave color="#f9fafb" className="bg-white" pattern="pattern3" />
      <section id="precios" className="py-20 bg-gray-50">
        <AnimatedSection className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">
                Planes sin Restricciones
              </h2>
              <p className="text-gray-600 max-w-3xl mx-auto mb-8">
                Todos nuestros planes incluyen acceso completo a todas las
                funcionalidades del sistema. Sin límites de usuarios, sin
                límites de equipos, sin límites de bodegas.
              </p>
              <div className="bg-gradient-to-r from-teal-50 to-teal-100 p-6 rounded-xl mb-12 max-w-3xl mx-auto">
                <h3 className="font-semibold text-teal-800 mb-4 text-lg">
                  Características incluidas en todos los planes:
                </h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <ul className="space-y-3">
                    <li className="flex items-center text-teal-700">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" />
                      <span>Usuarios ilimitados</span>
                    </li>
                    <li className="flex items-center text-teal-700">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" />
                      <span>Bodegas ilimitadas</span>
                    </li>
                    <li className="flex items-center text-teal-700">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" />
                      <span>Equipos ilimitados</span>
                    </li>
                  </ul>
                  <ul className="space-y-3">
                    <li className="flex items-center text-teal-700">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" />
                      <span>Soporte 24/7</span>
                    </li>
                    <li className="flex items-center text-teal-700">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" />
                      <span>Contabilidad completa</span>
                    </li>
                    <li className="flex items-center text-teal-700">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" />
                      <span>Actualizaciones gratuitas</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="inline-flex items-center justify-center bg-gray-100 rounded-full p-1.5">
                <button onClick={() => setIsAnnual(false)} className={`px-6 py-2 rounded-full font-medium transition-all duration-300 ${!isAnnual ? 'bg-white text-teal-600 shadow-md' : 'text-gray-600 hover:text-gray-900'}`}>
                  Mensual
                </button>
                <button onClick={() => setIsAnnual(true)} className={`px-6 py-2 rounded-full font-medium transition-all duration-300 flex items-center gap-2 ${isAnnual ? 'bg-white text-teal-600 shadow-md' : 'text-gray-600 hover:text-gray-900'}`}>
                  Anual
                  <span className={`inline-block px-2 py-0.5 text-xs rounded-full transition-all duration-300 ${isAnnual ? 'bg-teal-100 text-teal-600' : 'bg-gray-200 text-gray-600'}`}>
                    15% dto.
                  </span>
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-lg" whileHover={{
              y: -5,
              transition: {
                duration: 0.3
              }
            }}>
                <div className="p-8 bg-white">
                  <h3 className="text-2xl font-bold mb-2">Servicios</h3>
                  <p className="text-gray-600 mb-4">
                    Ideal para empresas que solo ofrecen servicios profesionales
                  </p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold">
                      {isAnnual ? '$306' : '$30'}
                    </span>
                    <span className="text-gray-500">
                      /{isAnnual ? 'año' : 'mes'}
                    </span>
                    {isAnnual && <div className="text-sm text-teal-600 mt-1">
                        <span className="line-through text-gray-400">$360</span>
                        <span className="text-teal-600"> → $306</span>
                        <div className="text-teal-600">Ahorro de $54/año</div>
                      </div>}
                  </div>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Acceso completo al sistema</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Facturación electrónica</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Gestión de servicios</span>
                    </li>
                  </ul>
                  <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me gustaría solicitar una demo gratuita de QuickCont para conocer todas sus funcionalidades y ver cómo puede ayudar a mi empresa. ¿Podrían agendar una demostración personalizada?" target="_blank" rel="noopener noreferrer" whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} className="block w-full py-3 px-4 rounded-xl font-semibold bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-center">
                    Solicitar mes gratuito
                  </motion.a>
                </div>
              </motion.div>
              {/* Multifuncional Plan (Center) */}
              <motion.div className="relative rounded-2xl overflow-hidden border-2 border-teal-500 shadow-xl" whileHover={{
              y: -5,
              transition: {
                duration: 0.3
              }
            }}>
                <div className="absolute top-0 left-0 right-0 bg-teal-500 text-white text-center py-1 text-sm font-medium">
                  Más popular
                </div>
                <div className="p-8 bg-white pt-10">
                  <h3 className="text-2xl font-bold mb-2">Multifuncional</h3>
                  <p className="text-gray-600 mb-4">
                    Para empresas grandes con múltiples operaciones
                  </p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold">
                      {isAnnual ? '$612' : '$60'}
                    </span>
                    <span className="text-gray-500">
                      /{isAnnual ? 'año' : 'mes'}
                    </span>
                    {isAnnual && <div className="text-sm text-teal-600 mt-1">
                        <span className="line-through text-gray-400">$720</span>
                        <span className="text-teal-600"> → $612</span>
                        <div className="text-teal-600">Ahorro de $108/año</div>
                      </div>}
                  </div>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Acceso completo al sistema</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Múltiples sucursales</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Operaciones complejas</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>APIs para integraciones</span>
                    </li>
                  </ul>
                  <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me interesa el Plan Multifuncional de QuickCont para mi empresa. Quisiera información detallada sobre todas las funcionalidades y el proceso de implementación." target="_blank" rel="noopener noreferrer" whileHover={{
                  scale: 1.03
                }} whileTap={{
                  scale: 0.98
                }} className="block w-full py-3 px-4 rounded-xl font-semibold bg-teal-600 text-white hover:bg-teal-700 transition-colors text-center">
                    Comenzar mes gratuito
                  </motion.a>
                </div>
              </motion.div>
              <motion.div className="relative rounded-2xl overflow-hidden border border-gray-200 shadow-lg" whileHover={{
              y: -5,
              transition: {
                duration: 0.3
              }
            }}>
                <div className="p-8 bg-white">
                  <h3 className="text-2xl font-bold mb-2">Inventario</h3>
                  <p className="text-gray-600 mb-4">
                    Perfecto para empresas que manejan inventario
                  </p>
                  <div className="mb-6">
                    <span className="text-4xl font-bold">
                      {isAnnual ? '$459' : '$45'}
                    </span>
                    <span className="text-gray-500">
                      /{isAnnual ? 'año' : 'mes'}
                    </span>
                    {isAnnual && <div className="text-sm text-teal-600 mt-1">
                        <span className="line-through text-gray-400">$540</span>
                        <span className="text-teal-600"> → $459</span>
                        <div className="text-teal-600">Ahorro de $81/año</div>
                      </div>}
                  </div>
                  <ul className="space-y-3 mb-8">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Acceso completo al sistema</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Control de inventario</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-teal-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span>Gestión de bodegas</span>
                    </li>
                  </ul>
                  <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Estoy interesado en el Plan Inventario de QuickCont. Necesito una solución para gestionar mi inventario y me gustaría conocer más detalles sobre la prueba gratuita." target="_blank" rel="noopener noreferrer" whileHover={{
                  scale: 1.03
                }} whileTap={{
                  scale: 0.98
                }} className="block w-full py-3 px-4 rounded-xl font-semibold bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors text-center">
                    Comenzar mes gratuito
                  </motion.a>
                </div>
              </motion.div>
            </div>
            <div className="mt-12 text-center">
              <p className="text-gray-600 mb-4">
                ¿Tienes preguntas sobre nuestros planes?
              </p>
              <Link to="/contact">
                <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me gustaría obtener más información sobre QuickCont y sus planes personalizados para mi empresa. ¿Podrían ayudarme a encontrar la mejor solución?" target="_blank" rel="noopener noreferrer" whileHover={{
                scale: 1.05
              }} whileTap={{
                scale: 0.95
              }} className="inline-flex items-center px-6 py-3 border border-teal-600 text-teal-600 rounded-xl hover:bg-teal-50 transition-colors font-medium">
                  Contacta con nuestro equipo de ventas
                  <ArrowRight className="ml-2 h-5 w-5" />
                </motion.a>
              </Link>
            </div>
          </div>
        </AnimatedSection>
      </section>

      <Wave color="white" className="bg-gray-50" pattern="pattern1" />
      <section id="soporte" className="py-20 bg-white">
        <AnimatedSection className="w-full">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Soporte Técnico</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Nuestro equipo de especialistas está disponible para ayudarte a
                resolver cualquier duda o problema técnico que puedas tener con
                QuickCont.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
              {supportOptions.map((option, index) => <motion.div key={index} className="bg-white rounded-xl p-6 border border-gray-200 shadow-sm hover:shadow-md transition-shadow text-center" whileHover={{
              y: -5
            }}>
                  <div className="flex justify-center mb-4">{option.icon}</div>
                  <h3 className="text-xl font-semibold mb-2">{option.title}</h3>
                  <p className="text-gray-600">{option.description}</p>
                </motion.div>)}
            </div>
            <div className="bg-gray-50 rounded-2xl p-8 md:p-12">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
                <div>
                  <h3 className="text-2xl font-bold mb-4">
                    Centro de capacitación
                  </h3>
                  <p className="text-gray-600 mb-6">
                    Maximiza tu inversión con nuestros programas de capacitación
                    diseñados para usuarios de todos los niveles, desde
                    principiantes hasta expertos en contabilidad.
                  </p>
                  <ul className="space-y-3 mb-8">
                    {['Webinars semanales gratuitos', 'Videotutoriales paso a paso', 'Certificaciones oficiales', 'Sesiones de capacitación personalizadas'].map((item, i) => <li key={i} className="flex items-center">
                        <CheckCircle className="w-5 h-5 text-teal-600 mr-2 flex-shrink-0" />
                        <span>{item}</span>
                      </li>)}
                  </ul>
                  <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me interesa los recursos de capacitación, me gustaría obtener más información sobre el tema" target="_blank" rel="noopener noreferrer" whileHover={{
                  scale: 1.05
                }} whileTap={{
                  scale: 0.95
                }} className="inline-flex items-center px-6 py-3 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-medium">
                    Saber más sobre los recursos de capacitación
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </motion.a>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Capacitación en línea" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Equipo de soporte" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Documentación técnica" className="w-full h-48 object-cover" />
                  </div>
                  <div className="rounded-xl overflow-hidden shadow-md">
                    <img src="https://images.unsplash.com/photo-1603201667141-5a2d4c673378?w=400&auto=format&fit=crop&q=60&ixlib=rb-4.0.3" alt="Webinar en directo" className="w-full h-48 object-cover" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </AnimatedSection>
      </section>
      <Wave color="#f9fafb" className="bg-white" pattern="pattern2" />
      <AnimatedSection className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-8">
            ¿Listo para optimizar tu gestión financiera?
          </h2>
          <p className="text-gray-600 mb-8 max-w-2xl mx-auto">
            Únete a miles de empresas que ya han transformado su contabilidad
            con QuickCont. Prueba gratis durante 30 días sin compromiso.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <motion.a href="https://wa.me/593979652188?text=¡Hola! 👋 Me interesa tener una demostración, me gustaría obtener más información sobre el tema" target="_blank" rel="noopener noreferrer" whileHover={{
            scale: 1.05
          }} whileTap={{
            scale: 0.95
          }} className="bg-teal-600 text-white px-8 py-4 rounded-xl font-semibold hover:bg-teal-700 transition-colors flex items-center justify-center">
              <Laptop className="w-5 h-5 mr-2" />
              Solicitar Demo Gratuita
            </motion.a>
          </div>
        </div>
      </AnimatedSection>
    </ProductLayout>;
};